import type { ChatEntry, ChatMessage } from './types';

/**
 * Build the outgoing chat history from the rendered conversation entries.
 *
 * - Drops `displayOnly` entries (the seeded greeting and failed/error assistant turns).
 * - Normalises to STRICTLY ALTERNATING roles: when two same-role turns end up adjacent
 *   (e.g. after a failed answer left an unanswered user turn), only the last of that run is
 *   kept, and any dangling leading `assistant` turn is dropped.
 *
 * The result always alternates user/assistant and ends on the newest user message, so a
 * failed turn never produces consecutive `user` messages in the payload.
 */
export function buildRequestMessages(entries: ChatEntry[]): ChatMessage[] {
  const collapsed: ChatMessage[] = [];

  for (const entry of entries) {
    if (entry.displayOnly) continue;

    const message: ChatMessage = { role: entry.role, content: entry.content };
    const last = collapsed[collapsed.length - 1];

    if (last && last.role === message.role) {
      // Same role twice in a row → keep only the latest (drops unanswered/failed turns).
      collapsed[collapsed.length - 1] = message;
    } else {
      collapsed.push(message);
    }
  }

  // History must start with a user turn.
  while (collapsed[0]?.role === 'assistant') {
    collapsed.shift();
  }

  return collapsed;
}
